/**
 * ProblemTypeGraph — an interactive traverser over the problem-type navigation graph.
 * You stand on one problem type (its member questions shown) and hop along its typed
 * edges (harder/easier-variant, prerequisite, follow-up, similar-concept) to neighbouring
 * types. A breadcrumb trail records the path. Two entry points land here: the Problem-Types
 * list, and the "View in graph" link on a question. See docs/FeatureDesigns/problem-type-similarity.md §4.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LatexText } from '@shared-ui/latex';
import { adminApi } from '../api/adminApi';
import type { ProblemTypeGraphEdge } from '../types';

const relColor: Record<string, string> = {
  'harder-variant': '#b91c1c', 'easier-variant': '#15803d',
  'prerequisite': '#7c3aed', 'follow-up': '#0369a1', 'similar-concept': '#b45309',
};
const REL_ORDER = ['harder-variant', 'easier-variant', 'prerequisite', 'follow-up', 'similar-concept'];
const REL_HELP: Record<string, string> = {
  'harder-variant': 'same concept, one band up', 'easier-variant': 'same concept, one band down',
  'prerequisite': 'must be understood first', 'follow-up': 'unlocked next', 'similar-concept': 'adjacent concept, same band',
};
const bandColor = (b?: string): string =>
  /easy/i.test(b || '') ? '#15803d' : /hard/i.test(b || '') ? '#b91c1c' : /med/i.test(b || '') ? '#b45309' : 'var(--text-secondary)';
const shortLabel = (l?: string, id?: string): string => {
  const s = l ?? id ?? '?'; const p = s.split(' · '); return p.length > 2 ? p.slice(2).join(' · ') : s;
};

export function ProblemTypeGraph() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const subject = params.get('subject') || 'physics';
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['problem-type-node', subject, id],
    queryFn: () => adminApi.getProblemTypeNode(id, subject),
    enabled: !!id,
  });

  // breadcrumb trail across hops (component stays mounted as :id changes)
  const [trail, setTrail] = useState<{ id: string; label: string }[]>([]);
  useEffect(() => {
    if (!data) return;
    const label = shortLabel(data.type.label, data.type.id);
    setTrail(prev => {
      const at = prev.findIndex(n => n.id === data.type.id);
      if (at >= 0) return prev.slice(0, at + 1);            // revisit → truncate to it
      return [...prev, { id: data.type.id, label }];
    });
  }, [data]);

  const go = (typeId: string) => navigate(`/problem-types/${typeId}?subject=${subject}`);

  const edgesByRel = useMemo(() => {
    const m: Record<string, ProblemTypeGraphEdge[]> = {};
    for (const e of data?.edgesOut ?? []) (m[e.relationType] ??= []).push(e);
    for (const arr of Object.values(m)) arr.sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0));
    return m;
  }, [data]);

  if (isLoading) return <div style={{ padding: 20, color: 'var(--text-secondary)' }}>Loading…</div>;
  if (error || !data) return <div style={{ padding: 20, color: '#b91c1c' }}>Could not load this problem type. <Link to="/problem-types">← all types</Link></div>;

  const { type, members } = data;

  return (
    <div style={{ padding: 16, maxWidth: 1000, margin: '0 auto' }}>
      {/* breadcrumb */}
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
        <Link to={`/problem-types?subject=${subject}`} style={{ color: 'var(--color-primary)' }}>All types</Link>
        {trail.map((n, i) => (
          <span key={n.id}>
            <span style={{ margin: '0 4px' }}>›</span>
            {i === trail.length - 1
              ? <b style={{ color: 'var(--text-primary)' }}>{n.label}</b>
              : <button onClick={() => go(n.id)} style={linkBtn}>{n.label}</button>}
          </span>
        ))}
      </div>

      {/* node header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
        <span style={{ width: 12, height: 12, borderRadius: '50%', background: bandColor(type.difficultyBand), flex: 'none' }} />
        <h1 style={{ fontSize: 23, margin: 0 }}>{type.label ?? type.id}</h1>
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
        {type.difficultyBand ?? '—'} band · {type.size} questions · concept <code>{type.primaryConceptId ?? '—'}</code>
        {type.reviewStatus && type.reviewStatus !== 'auto' ? ` · ${type.reviewStatus}` : ''}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 20, alignItems: 'start' }}>
        {/* members */}
        <section>
          <h2 style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-secondary)', margin: '0 0 8px' }}>
            Questions in this type ({members.length})
          </h2>
          <div style={{ display: 'grid', gap: 6 }}>
            {members.map(m => (
              <div
                key={m.id}
                onClick={() => navigate(`/questions?id=${m.id}`)}
                title="Open question detail"
                style={{
                  border: '1px solid var(--border-color)', borderRadius: 8, padding: '8px 10px', fontSize: 15, cursor: 'pointer',
                  background: m.isRepresentative ? 'var(--bg-hover)' : 'transparent',
                }}
              >
                <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', marginBottom: 2 }}>
                  <span style={{ fontSize: 11, color: bandColor(m.difficulty) }}>{m.difficulty ?? '?'}</span>
                  {m.isRepresentative && <span style={{ fontSize: 11, color: 'var(--color-primary)', fontWeight: 700 }}>★ representative</span>}
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-secondary)' }}>open ›</span>
                </div>
                <div style={{ color: 'var(--text-primary)' }}>
                  {m.questionText ? <LatexText text={m.questionText} /> : '(no text)'}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* edges */}
        <section>
          <h2 style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-secondary)', margin: '0 0 8px' }}>
            Traverse ({data.edgesOut.length} edges)
          </h2>
          {REL_ORDER.filter(r => edgesByRel[r]?.length).map(rel => (
            <div key={rel} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: relColor[rel] ?? '#888', marginBottom: 4 }}>
                {rel} <span style={{ fontWeight: 400, textTransform: 'none', color: 'var(--text-secondary)' }}>· {REL_HELP[rel]}</span>
              </div>
              <div style={{ display: 'grid', gap: 4 }}>
                {edgesByRel[rel].map(e => (
                  <button
                    key={e.id}
                    onClick={() => e.toType && go(e.toType.id)}
                    disabled={!e.toType}
                    style={{
                      textAlign: 'left', border: '1px solid var(--border-color)', borderLeft: `3px solid ${relColor[rel] ?? '#888'}`,
                      borderRadius: '0 8px 8px 0', padding: '6px 10px', background: 'transparent', cursor: e.toType ? 'pointer' : 'default', width: '100%',
                    }}
                  >
                    <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
                      <span style={{ width: 9, height: 9, borderRadius: '50%', background: bandColor(e.toType?.difficultyBand), flex: 'none' }} />
                      <span style={{ color: 'var(--text-primary)', fontSize: 14 }}>{shortLabel(e.toType?.label, e.toType?.id)}</span>
                      <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
                        {e.toType?.size ?? 0}q{e.confidence != null ? ` · ${e.confidence.toFixed(2)}` : ''}
                      </span>
                    </div>
                    {e.reason && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{e.reason}</div>}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {!data.edgesOut.length && <div style={{ color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: 13 }}>No outgoing edges from this type.</div>}
        </section>
      </div>
    </div>
  );
}

const linkBtn: React.CSSProperties = {
  border: 'none', background: 'transparent', color: 'var(--color-primary)', cursor: 'pointer', fontSize: 12, padding: 0,
};
